import * as THREE from 'three';

export type EquipmentBadgeTexture = {
  texture: THREE.CanvasTexture;
  /** Plate width / height, so callers can size the plane without distortion. */
  aspect: number;
};

const cache = new Map<string, EquipmentBadgeTexture>();

/** Black plate + white bold text, matching the EQ2 nameplate on the reference. */
const PLATE_COLOR = '#111111';
const PLATE_EDGE = '#2A2A2A';
const TEXT_COLOR = '#F4F4F4';

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * Cached equipment nameplate texture (e.g. "EQP-01").
 * Shared across instances — callers must not dispose the returned texture.
 */
export function getEquipmentBadgeTexture(text: string): EquipmentBadgeTexture {
  const label = (text || 'EQP').trim().toUpperCase().slice(0, 14);
  const cached = cache.get(label);
  if (cached) return cached;

  const fontPx = 72;
  const padX = fontPx * 0.55;
  const padY = fontPx * 0.42;

  const measure = document.createElement('canvas').getContext('2d');
  const font = `700 ${fontPx}px ui-sans-serif, system-ui, sans-serif`;
  let textW = label.length * fontPx * 0.6;
  if (measure) {
    measure.font = font;
    textW = measure.measureText(label).width;
  }

  const w = Math.ceil(textW + padX * 2);
  const h = Math.ceil(fontPx + padY * 2);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');

  if (ctx) {
    ctx.clearRect(0, 0, w, h);
    const inset = Math.max(2, h * 0.05);
    roundRect(ctx, inset, inset, w - inset * 2, h - inset * 2, h * 0.22);
    ctx.fillStyle = PLATE_COLOR;
    ctx.fill();
    ctx.strokeStyle = PLATE_EDGE;
    ctx.lineWidth = Math.max(2, h * 0.045);
    ctx.stroke();

    ctx.font = font;
    ctx.fillStyle = TEXT_COLOR;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, w / 2, h / 2 + h * 0.02);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  texture.needsUpdate = true;

  const result: EquipmentBadgeTexture = { texture, aspect: w / h };
  cache.set(label, result);
  return result;
}
